import React from 'react';
import { ZonedDateTimeRangeDto } from '@api/ChallengeClient';
import { FieldLabel } from '@components/textfield';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import {
    AttributionDayField,
    defaultAttributionDay,
    isDayInRange
} from './attribution-day-field';
import {
    ChallengeResultFormat,
    formatChallengeResult,
    storedValueDiffersFromDisplay,
    storedValueUnitLabel
} from './raw-value';
import { ChallengeLeaderboardEntry } from './types';

interface EditResultDialogProps {
    entry: ChallengeLeaderboardEntry | null;
    resultFormat: ChallengeResultFormat;
    requiresDay?: boolean;
    dateTimeRange?: ZonedDateTimeRangeDto;
    loading?: boolean;
    onClose(): void;
    onSubmit(
        subscriptionId: number,
        value: number,
        day?: string
    ): Promise<void>;
}

export function EditResultDialog({
    entry,
    resultFormat,
    requiresDay,
    dateTimeRange,
    loading,
    onClose,
    onSubmit
}: EditResultDialogProps) {
    const [value, setValue] = React.useState('');
    const [day, setDay] = React.useState('');
    const inputId = React.useId();

    React.useEffect(() => {
        if (!entry) {
            setValue('');
            setDay('');
            return;
        }
        setValue(String(entry.result));
        setDay(defaultAttributionDay(dateTimeRange));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry]);

    const enteredValue = Number(value);
    const valueInvalid =
        value.trim() === '' || Number.isNaN(enteredValue) || enteredValue < 0;
    const dayInvalid =
        Boolean(requiresDay) && !isDayInRange(day, dateTimeRange);
    const invalid = valueInvalid || dayInvalid;

    return (
        <Dialog
            open={Boolean(entry)}
            onOpenChange={(next) => !next && onClose()}
        >
            <DialogContent className="sm:max-w-xs">
                <DialogHeader>
                    <DialogTitle>Edit Result</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-1.5">
                    <FieldLabel
                        htmlFor={inputId}
                        label={`Result (${storedValueUnitLabel(resultFormat)})`}
                    />
                    <Input
                        id={inputId}
                        type="number"
                        min={0}
                        step="any"
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                    />
                    {storedValueDiffersFromDisplay(resultFormat) &&
                        !valueInvalid && (
                            <p className="text-xs text-muted-foreground">
                                {`Displays as ${formatChallengeResult(
                                    enteredValue,
                                    resultFormat
                                )}`}
                            </p>
                        )}
                </div>
                {requiresDay && (
                    <AttributionDayField
                        value={day}
                        range={dateTimeRange}
                        onChange={setDay}
                    />
                )}
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!entry || invalid}
                        loading={loading}
                        onClick={() => {
                            if (!entry) {
                                return;
                            }
                            void onSubmit(
                                entry.subscriptionId,
                                enteredValue,
                                requiresDay ? day : undefined
                            );
                        }}
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
